<template>
  <div>
    <v-layout>
      <v-col cols="3">
        <filter-comp
          :hide_date_filter="true"
          :hide_sort_filter="true"
          :hide_account_filter="true"
          view="user"
          @apply_filters="applyFilters($event.filters, $event.active_only)"
        ></filter-comp>
      </v-col>
      <v-col>
        <div v-if="users">
          <v-data-table
            id="user-data-table"
            :headers="headers"
            :items="users"
            item-key="user_id"
            class="elevation-1 row-pointer"
            :loading="loading"
            hide-default-footer
            @click:row="viewUser($event.account_id, $event.user_id)"
          >
            <template v-slot:top>
              <v-row justify="end">
                <v-btn
                  :to="{ name: 'user-create' }"
                  elevation="4"
                  color="primary"
                  class="ma-4 button_text--text"
                  @click="show_filter_dialog = true"
                >
                  Add User
                </v-btn>
              </v-row>
            </template>

            <template v-slot:[`item.account_name`]="{ item }">
              {{ getAccountDetails(item.account_id).name }}
            </template>

            <template v-slot:[`item.country_code`]="{ item }">
              {{ get_country_name(item.country_code) }}
            </template>

            <template v-slot:[`item.admin`]="{ item }">
              <v-simple-checkbox
                v-model="item.admin"
                disabled
              ></v-simple-checkbox>
            </template>

            <template v-slot:[`item.active`]="{ item }">
              <v-simple-checkbox
                v-model="item.active"
                disabled
              ></v-simple-checkbox>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <user-actions
                :user="item"
                show_view
                show_account
                show_usage
                show_orders
              ></user-actions>
            </template>

            <template v-slot:footer>
              <div><v-divider></v-divider></div>
              <v-layout justify-end>
                <paging-comp
                  :paging_id="getPagingId()"
                  :has_more="has_more"
                  @next_page="$emit('next_page', { last_id: $event.last_id })"
                  @prev_page="$emit('prev_page', { first_id: $event.first_id })"
                  ref="pagingComp"
                ></paging-comp>
              </v-layout>
            </template>
          </v-data-table>
        </div>
      </v-col>
    </v-layout>
  </div>
</template>

<style scoped>
.value-pointer {
  cursor: pointer;
}
</style>

<script>
import adminApi from "@/api/admin";
import UserActions from "@/components/user/UserActions.vue";
import FilterComp from "@/components/shared/Filter.vue";
import PagingComp from "@/components/shared/Paging.vue";
import countries from "@/app/countries";
import { mapGetters } from "vuex";
import utils from "@/utils/userType";

export default {
  components: {
    UserActions,
    FilterComp,
    PagingComp,
  },
  props: ["users", "has_more", "loading"],
  data() {
    return {
      accounts: {},
      account_promises: {},
    };
  },
  computed: {
    ...mapGetters(["user_details"]),
    is_super_or_support() {
      return utils.is_super_or_support(this.user_details);
    },
    headers() {
      return [
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Account", value: "account_name" },
        { text: "Job Title", value: "job_title" },
        { text: "Country", value: "country_code" },
        { text: "Admin?", value: "admin" },
        { text: "Active?", value: "active" },
        { text: "Actions", value: "actions", sortable: false },
      ];
    },
  },
  methods: {
    viewUser(account_id, user_id) {
      this.$router.push({
        name: "user-details",
        params: { account_id: account_id, user_id: user_id },
      });
    },

    viewAccount(account_id) {
      this.$router.push({
        name: "account-details",
        params: { account_id: account_id },
      });
    },

    getAccountDetails(account_id) {
      if (!this.account_promises[account_id]) {
        // cache the promises so we're not making requests for the same account
        this.account_promises[account_id] = adminApi
          .get_account(account_id)
          .then((response) => {
            this.accounts[account_id] = response.data.account;
            // TODO: there's likely a better way
            this.$forceUpdate(); // things will likely have already rendered
          })
          .catch((error) => {
            return error;
          });
      }

      return this.accounts[account_id] || {};
    },

    get_country_name(country_code) {
      if (country_code) {
        let country = countries.countries.filter(
          (item) => item.abbr === country_code
        );
        return `${country_code} - ${country[0].name}`;
      } else {
        return null;
      }
    },

    viewUserUsage(account_id, user_id) {
      this.$router.push({
        name: "user-usage",
        params: { account_id: account_id, user_id: user_id },
      });
    },

    applyFilters(filters, active_only) {
      // reset paging and push query params to router
      this.$refs.pagingComp.refreshPaging();
      this.$router
        .push({ query: { filters: filters, active_only: active_only } })
        .catch(() => {
          // catch redundant path error
        });
    },

    getPagingId() {
      let paging_id = "";
      if (this.users.length > 0) {
        this.user_id = this.users.slice(-1)[0]["user_id"];
        if (this.is_super_or_support && this.$route.name === "user-list") {
          this.account_id = this.users.slice(-1)[0]["account_id"];

          paging_id = this.account_id + ":" + this.user_id;
        } else {
          paging_id = this.user_id;
        }
      }
      return paging_id;
    },
  },
};
</script>
<style lang="css" scoped>
.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>
